/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import {
  useCallback,
  useEffect,
  useState,
  useImperativeHandle,
  type ReactNode,
  type RefAttributes
} from "react";
import { Button, LoadingBar } from "@design-lab/shared/ui";
import { useDropzone, type FileRejection } from "react-dropzone";
import {
  useArtwork,
  useArtworkImage,
  useEnhancedImageSource,
  ImageUploadedBanner
} from "@design-lab/entities/artwork";
import { isUserLoggedIn } from "@design-lab/entities/user";
import { AlertCircle, Shirt, Trash2, X } from "lucide-react";
import type { Side } from "@design-lab/shared";
import { ARTWORK_PROCESSING } from "@design-lab/shared/constants";
import {
  validateDesignLabArtworkFile,
  DESIGN_LAB_ACCEPT_CONFIG,
  MAX_FILE_SIZE_BYTES,
  MIN_IMAGE_DIMENSION_PX
} from "common/artwork-file-types";
import { useNotification } from "@design-lab/shared/ui/notification";
import { useDesignProjectSync } from "@design-lab/features/design-project/model/useDesignProjectSync";
// eslint-disable-next-line no-restricted-imports
import { MyDesignLibrary } from "@design-lab/widgets/design-lab-upload-artwork/ui/MyDesignLibrary";
import { CanvaButton } from "./Canva/CanvaButton";

export type UploadErrorState = { fileName: string; reason: string };

// Derived from the shared validation config so the requirements hint stays in
// sync with what the validator actually accepts (formats, size, min dimension).
const ACCEPTED_FORMAT_LABELS = Object.keys(DESIGN_LAB_ACCEPT_CONFIG)
  .map(mimeType => (mimeType.split("/")[1] ?? mimeType).replace("+xml", "").toUpperCase())
  .join(", ");
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE_BYTES / (1024 * 1024);
const UPLOAD_REQUIREMENTS_TEXT = `Accepted: ${ACCEPTED_FORMAT_LABELS} · Max ${MAX_FILE_SIZE_MB}MB · Largest side ≥ ${MIN_IMAGE_DIMENSION_PX}px`;

export type ImageSelectorProps = {
  side?: Side;
  title?: ReactNode;
  onFileDrop?: (files: File[]) => Promise<void>;
  isLoggedIn?: boolean;
  shouldOpenLibrary?: boolean;
  onLibraryAutoOpenComplete?: () => void;
  shouldOpenCanvaFromParent?: boolean;
  onCanvaAutoOpenCompleteFromParent?: () => void;
  onCanvaFileReady?: (file: File) => Promise<void>;
  onLibraryDesignSelect?: (artworkId: number, fileType?: string) => void;
  isWaitingForCatalogNumber?: boolean;
  allowMultipleFiles?: boolean;
  maxSelectionCount?: number;
  uploadError?: UploadErrorState | null;
  onRetryUpload?: () => void;
  onChooseDifferentFile?: () => void;
  onDismissUploadError?: () => void;
};

export type ImageSelectorRef = {
  openDevice: () => void;
  openLibrary: () => void;
  openCanva: () => void;
};

/**
 * Render a configurable image selection UI that lets users pick or create designs from device, My Design Library, Canva, or AI Studio and displays upload / processing states.
 *
 * The component validates and handles file drops (or delegates via `onFileDrop`), auto-opens library or Canva when requested, exposes an imperative handle with `openDevice`, `openLibrary`, and `openCanva`, and shows contextual states: an uploaded image banner, a processing/loading view with delete action, or the main drag-and-drop action panel. It also supports multiple file selection, backward-compatible library selection callbacks, and an optional `onClearSelection` to clear the current selection.
 *
 * @returns A React element rendering the image selector UI and any related modals.
 */
export function ImageSelector(props: ImageSelectorProps & RefAttributes<ImageSelectorRef>) {
  const {
    side = "front",
    title,
    onFileDrop,
    isLoggedIn: propIsLoggedIn,
    shouldOpenLibrary,
    onLibraryAutoOpenComplete,
    shouldOpenCanvaFromParent,
    onCanvaAutoOpenCompleteFromParent,
    ref,
    onCanvaFileReady,
    onLibraryDesignSelect,
    allowMultipleFiles = false,
    maxSelectionCount = 10,
    uploadError,
    onRetryUpload,
    onChooseDifferentFile,
    onDismissUploadError
  } = props;
  const isLoggedIn = propIsLoggedIn ?? isUserLoggedIn();
  const { uploadArtwork, deleteArtwork } = useArtworkImage();
  const { getMachineBySide } = useArtwork();
  const machine = getMachineBySide(side);
  const { artworkId } = useEnhancedImageSource({ side });
  const { error: notifyError } = useNotification();

  const [isMyDesignLibraryOpen, setIsMyDesignLibraryOpen] = useState(false);
  const [shouldOpenCanva, setShouldOpenCanva] = useState(false);

  const onArtworkRemove = useCallback(() => {
    deleteArtwork(side, artworkId);
  }, [deleteArtwork, side, artworkId]);

  const handleFileDrop = useCallback(
    async (droppedFiles: File[]) => {
      const file = droppedFiles[0];
      if (!file) return;

      if (onFileDrop) {
        // Parent (DesignLabImageSelector) validates, uploads, and owns error state.
        await onFileDrop(droppedFiles);
        return;
      }

      const { valid, message, file: materializedFile } = await validateDesignLabArtworkFile(file);
      if (!valid) {
        notifyError({ title: "Upload Failed", description: message });
        return;
      }

      // Use the materialized file for upload (handles cloud storage providers on mobile)
      const fileToUpload = materializedFile || file;
      uploadArtwork({ file: fileToUpload, side });
    },
    [uploadArtwork, side, notifyError, onFileDrop]
  );

  // Files rejected by the dropzone's `accept` filter (e.g. AVIF/TXT) never reach
  // handleFileDrop's accepted-files path. Route them through the same owner so a
  // persistent inline error is surfaced (and any prior error is replaced/cleared).
  const handleDropRejected = useCallback(
    async (fileRejections: FileRejection[]) => {
      const rejectedFile = fileRejections[0]?.file;
      if (!rejectedFile) return;

      // A "too-many-files" rejection must reject the whole batch — never forward one
      // of the files for upload, or we'd silently bypass the single-file constraint.
      const isTooManyFiles = fileRejections.some(rejection =>
        rejection.errors.some(error => error.code === "too-many-files")
      );
      if (isTooManyFiles) {
        notifyError({ title: "Upload Failed", description: "Please upload one file at a time." });
        return;
      }

      if (onFileDrop) {
        await onFileDrop([rejectedFile]);
        return;
      }

      notifyError({ title: "Upload Failed", description: "You can't upload files of this type" });
    },
    [onFileDrop, notifyError]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: DESIGN_LAB_ACCEPT_CONFIG,
    multiple: allowMultipleFiles,
    noClick: true,
    noKeyboard: true,
    onDrop: handleFileDrop,
    onDropRejected: handleDropRejected
  });

  // Expose methods via imperative handle
  useImperativeHandle(
    ref,
    () => ({
      openDevice: open,
      openLibrary: () => setIsMyDesignLibraryOpen(true),
      openCanva: () => setShouldOpenCanva(true)
    }),
    [open]
  );

  // Handle auto-open library
  useEffect(() => {
    if (shouldOpenLibrary && isLoggedIn) {
      setIsMyDesignLibraryOpen(true);
      onLibraryAutoOpenComplete?.();
    }
  }, [shouldOpenLibrary, isLoggedIn, onLibraryAutoOpenComplete]);

  // Handle auto-open canva from parent
  useEffect(() => {
    if (shouldOpenCanvaFromParent && isLoggedIn) {
      setShouldOpenCanva(true);
      onCanvaAutoOpenCompleteFromParent?.();
    }
  }, [shouldOpenCanvaFromParent, isLoggedIn, onCanvaAutoOpenCompleteFromParent]);

  const onCanvaAutoOpenComplete = () => {
    setShouldOpenCanva(false);
  };

  const handleLibraryClose = useCallback(() => {
    setIsMyDesignLibraryOpen(false);
  }, []);

  const handleLibrarySelect = useCallback(
    (artworks: Array<{ id: number; fileType?: string }>) => {
      setIsMyDesignLibraryOpen(false);
      // For backward compatibility, if onLibraryDesignSelect expects a single ID
      if (artworks.length === 1 && artworks[0]) {
        onLibraryDesignSelect?.(artworks[0].id, artworks[0].fileType);
      }
    },
    [onLibraryDesignSelect]
  );

  const handleCanvaFilesReady = useCallback(
    async (files: File[]) => {
      if (onCanvaFileReady) {
        if (files[0]) {
          await onCanvaFileReady(files[0]);
        }
      } else {
        if (!files[0]) return;
        const {
          valid,
          message,
          file: materializedFile
        } = await validateDesignLabArtworkFile(files[0]);
        if (!valid) {
          notifyError({ title: "Upload Failed", description: message });
          return;
        }
        // Use the materialized file for upload
        const fileToUpload = materializedFile || files[0];
        uploadArtwork({ file: fileToUpload, side });
      }
    },
    [onCanvaFileReady, uploadArtwork, side, notifyError]
  );

  // Design Lab specific logic - only show these states when not using generic props
  const { source } = useEnhancedImageSource({ side });
  const isFileUploaded = Boolean(machine.state.context.artworkId);
  const { file } = machine.state.context;
  const { showModal } = useDesignProjectSync();
  const isStartDesigningShown = showModal;

  // Show design lab specific states only when using the design lab functionality
  if (
    !onFileDrop &&
    !isStartDesigningShown &&
    machine.state.matches("finished") &&
    source.enhancedImage
  ) {
    return <ImageUploadedBanner image={{ url: source.enhancedImage.base64 }} side={side} />;
  }

  if (!onFileDrop && !isStartDesigningShown && isFileUploaded && file) {
    return <ImageUploadedBanner image={{ url: URL.createObjectURL(file) }} side={side} />;
  }

  if (!onFileDrop && !isStartDesigningShown && machine.state.hasTag("loading")) {
    return (
      <div className="flex flex-1 flex-col gap-2">
        <LoadingBar
          side={side}
          startTime={machine.state.context.startTime}
          estimatedDuration={ARTWORK_PROCESSING.ESTIMATED_DURATION}
        />
        {isFileUploaded && (
          <div className="flex justify-center">
            <Button
              data-testid="delete-image-button"
              variant="outline"
              size="sm"
              onPress={onArtworkRemove}
            >
              <Trash2 className="size-4 text-red-600" />
              Delete image
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <section
        onDragEnter={getRootProps().onDragEnter}
        onDragOver={getRootProps().onDragOver}
        onDrop={getRootProps().onDrop}
        className="flex h-max w-full flex-1 border-spacing-12 flex-col items-center justify-center gap-2 rounded-md bg-blue-50 p-3 lg:gap-4 lg:border-2 lg:border-dashed lg:border-blue-700"
      >
        <div className="flex items-center gap-2">
          <Shirt className="size-6" />
          {title && <p className="font-bold">{title}</p>}
        </div>
        <p className="font-medium">Drag your design here or choose a design from</p>

        <input
          {...getInputProps({
            "aria-label": "Upload artwork file",
            "data-testid": "artwork-file-input-dropzone"
          })}
        />
        {uploadError && (
          <div
            data-testid="upload-error-panel"
            role="alert"
            className="w-full rounded-md border border-red-300 bg-red-50 p-3 text-left"
          >
            <div className="flex items-start gap-2">
              <AlertCircle aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-red-600" />
              <div className="flex-1">
                <p className="font-semibold text-red-800">Upload failed</p>
                <p
                  data-testid="upload-error-filename"
                  title={uploadError.fileName}
                  className="truncate text-sm text-red-800"
                >
                  {uploadError.fileName}
                </p>
                <p data-testid="upload-error-reason" className="text-sm text-red-700">
                  {uploadError.reason}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    data-testid="upload-error-retry"
                    size="sm"
                    onPress={() => onRetryUpload?.()}
                  >
                    Try again
                  </Button>
                  <Button
                    data-testid="upload-error-choose-different"
                    size="sm"
                    variant="outline"
                    onPress={() => onChooseDifferentFile?.()}
                  >
                    Choose different file
                  </Button>
                </div>
                <p data-testid="upload-error-requirements" className="mt-2 text-xs text-red-700">
                  {UPLOAD_REQUIREMENTS_TEXT}
                </p>
              </div>
              <button
                type="button"
                aria-label="Dismiss upload error"
                data-testid="upload-error-dismiss"
                onClick={() => onDismissUploadError?.()}
                className="focus-visible:ring-ring cursor-pointer rounded text-red-600 hover:text-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        )}
        <div className="mx-auto grid max-w-md grid-cols-2 justify-items-center gap-3 lg:flex lg:max-w-full lg:flex-wrap lg:justify-center [&>*:nth-child(odd):last-child]:col-span-2">
          <Button
            data-testid="my-device-button"
            size="lg"
            onPress={open}
            className="relative w-full max-w-40 py-2 text-sm lg:shrink"
          >
            My Device
          </Button>

          {isLoggedIn && (
            <>
              <Button
                data-testid="my-library-button"
                size="lg"
                variant="outline"
                onPress={() => setIsMyDesignLibraryOpen(true)}
                className="w-full max-w-40 border-black/20 py-2 text-sm lg:shrink"
              >
                My Design Library
              </Button>
              <CanvaButton
                autoOpen={shouldOpenCanva}
                onAutoOpenComplete={onCanvaAutoOpenComplete}
                onFilesReady={handleCanvaFilesReady}
              />
            </>
          )}
        </div>
      </section>
      {isMyDesignLibraryOpen && (
        <MyDesignLibrary
          isOpen={isMyDesignLibraryOpen}
          onClose={handleLibraryClose}
          onSelectArtworks={handleLibrarySelect}
          allowMultipleSelection={allowMultipleFiles}
          maxSelectionCount={maxSelectionCount}
        />
      )}
    </>
  );
}
